import classNames from "classnames";
import BaseCard from "@/components/common/BaseCard";
import ChartData from "@components/HomeMain/ChartData";
import Sunrise from "@/components/common/Sunrise";
import { appThemeStyle } from "@/constants/theme.config";
import { filterEmpty, pxChangeVw } from "@/utils";
import useDayjs from "@/hooks/useDayjs";
import useDateFormat from "@/hooks/useDateFormat";
import useTranslate from "@/hooks/useTranslate";
import { STORAGE_KEYS } from "@/constants/const_var";
import { useAdCpt1 } from "@/hooks/adCpt/daily";
import { scrollToTop } from "@/utils/scrollToTop";
import { is_en_gl_link, is_es_mx_link } from "@/i18n/lang";
import bus from "@/utils/mitt";
import { homeAtom } from "@/atoms/home";
import { headerAtom } from "@/atoms/app";
import { commonStateAtom } from "@/atoms/common";
import { getWeekName } from "@/utils/getWeekName";
import { getCurrentAdScenario } from "@/hooks/useAdScenario";

const countLines = (string: string = "", wordCount = 21): number => {
  if (!string) return 1;
  return Math.ceil(string.length / wordCount);
};

const DailyDetail: React.FC = () => {
  const adPosition27 = useAdCpt1("position-27");
  const adPosition40 = useAdCpt1("position-40");
  const adPosition28 = useAdCpt1("position-28");
  const adPosition37 = useAdCpt1("position-37");
  const adPosition29 = useAdCpt1("position-29");
  const adPosition38 = useAdCpt1("position-38");

  const { isTranTabAdScenario } = getCurrentAdScenario();
  const setHeaderData = useSetAtom(headerAtom);
  const setCommonData = useSetAtom(commonStateAtom);
  const locInfo = LocalStorage.getItem("INFO");
  const { dayjs, userTimeZone } = useDayjs();
  const homeData = useAtomValue(homeAtom);
  const params = useParams();
  const { state } = useLocation();
  const [activeIndex, setActiveIndex] = useState<number>(state.index || 0);
  const [dayItem, setDayItem] = useState<any>(state.dayList?.[state.index] || null);
  const deily_detail_ref = useRef<HTMLDivElement>(null);
  const { shortDate } = useDateFormat();
  const { $t } = useTranslate();
  const { scrollToCenter } = useScrollToCenter();
  const { pathname } = useLocation();
  const fetchDailyDetailData = useSetAtom(homeAtom);
  useEffect(() => {
    fetchDailyDetailData(pathname);
  }, []);
  bus.on("DAILY_DETAIL", (data: any) => {
    setDayItem(data[state.index]);
  });

  const handleDayClick = (index: number) => {
    setDayItem(state.dayList[index]);
    setActiveIndex(index);
  };

  useEffect(() => {
    const activeEl = deily_detail_ref.current?.children[activeIndex] as HTMLElement;
    scrollToCenter(activeEl, activeEl?.parentElement as HTMLElement);
  }, [activeIndex]);

  const weatherStatus = homeData?.data?.info?.weatherStatus || locInfo?.weatherStatus;
  const tipsStyle = appThemeStyle?.[weatherStatus]?.tipsStyle ?? {};

  useEffect(() => {
    if (state.dayList && state.dayList.length > 0) {
      scrollToTop();
      const initialIndex = params.index ?? state.index;
      setDayItem(state.dayList[initialIndex]);
      setActiveIndex(initialIndex);

      if (deily_detail_ref.current) {
        const scrollWidth = deily_detail_ref.current.scrollWidth;
        const length = state.dayList.length || 1;
        deily_detail_ref.current.scrollTo(initialIndex * (scrollWidth / length), 0);
      }
    }

    // 同步存储和状态
    const syncState = () => {
      if (homeData?.data.info) {
        CookiesStorage.setItem(STORAGE_KEYS.INFO, homeData?.data.info);
      }
      const info = CookiesStorage.getItem(STORAGE_KEYS.INFO);
      setCommonData((prev: any) => ({
        ...prev,
        data: {
          info,
        },
      }));
      const header2 = {
        WeatherIconImg: info?.WeatherIconImg ?? "",
        WeatherText: info?.WeatherText ?? "",
        Temperature: info?.Temperature ?? {},
        Wind: info?.Wind ?? {},
        IconId: info?.IconId ?? "",
      };
      if (!isEmptyType(filterEmpty(header2))) {
        LocalStorage.setItem("HEADER2", header2);
        setHeaderData(header2);
      }
      LocalStorage.setItem("INFO", info);
    };

    syncState();

    return () => {
      CookiesStorage.removeItem(STORAGE_KEYS.INFO);
    };
  }, []);
  const renderDaySelector = () => {
    const getFormattedDate = (item: any) => {
      if (item?.EpochDateTime) {
        if (userTimeZone) {
          if (is_en_gl_link() || is_es_mx_link()) {
            return dayjs.unix(item.EpochDateTime).tz(userTimeZone).format("DD/MM");
          }
          return dayjs.unix(item.EpochDateTime).tz(userTimeZone).format(shortDate);
        } else {
          item.DateShort;
        }
      }
      return "";
    };
    return (
      <div className="p-0 mt-16">
        <div
          ref={deily_detail_ref}
          className="overflow-x-auto overflow-y-hidden flex  gap-x-8 p-16 rounded-10 bg-[var(--app-bg-08)] backdrop-blur-10 rounded-0 bg-[var(--app-bg-12)]"
        >
          {state?.dayList?.map((item: { EpochDateTime: number; DateShort: any; Week: any }, index: number) => {
            const weekDayName = getWeekName(item.EpochDateTime);

            return (
              <div
                key={index}
                className={classNames([
                  "text-12 rounded-8 p-8 items-center",
                  index === activeIndex * 1 ? "bg-[var(--app-bg-05)]" : "bg-[var(--app-bg-13)]",
                ])}
                onClick={() => handleDayClick(index)}
              >
                <p>{weekDayName ?? item.Week}</p>
                <p>{getFormattedDate(item)}</p>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderWeatherPeriod = (period: "Day" | "Night", titleKey: string, tempKey: "Maximum" | "Minimum") => {
    const periodData = dayItem?.[period];
    if (!periodData) return null;
    const WeatherPeriodDetails = () => (
      <>
        <div>{$t(titleKey)}</div>
        <div
          className="flex items-stretch"
          style={{
            paddingBottom: pxChangeVw((countLines(periodData?.WeatherText) - 1) * 20 + "px"),
          }}
        >
          <div className="flex-[1] w-75 h-45 flex flex-col items-center translate-y-[-4px]">
            <WeatherImage className="w-56" src={periodData?.WeatherIconImg} />
            <p>{periodData?.WeatherText}</p>
          </div>
          <div className="flex-[1] text-66 center">
            <span>{dayItem?.Temperature?.[tempKey]?.Value}</span>
            <div className="flex flex-col justify-between items-center">
              <span className="text-30">{dayItem?.Temperature?.[tempKey]?.Unit}</span>
              <span className="text-10">{$t(`title${tempKey === "Maximum" ? 42 : 43}`)}</span>
            </div>
          </div>
        </div>
        <div
          className="rounded-8 font-300 text-10 color-[var(--app-text-14)] lh-13 p-8 box-border bg-[var(--app-bg-14)]"
          style={tipsStyle}
        >
          {periodData?.LongPhrase}
        </div>
      </>
    );
    if (!periodData?.Items?.length) {
      return null;
    }
    return (
      <WeatherContent
        className={classNames([
          " flex flex-col justify-start gap-y-10 bg-[var(--app-bg-08)] p-16 rounded-10 backdrop-blur-10",
        ])}
      >
        <WeatherPeriodDetails />
        <BaseCard itemList={periodData.Items} useTransparentStyle={true} />
      </WeatherContent>
    );
  };

  const renderWarnings = () => {
    if (!dayItem?.warnings?.length) return null;

    return dayItem.warnings.map((item: any, index: null | undefined) => {
      const warning = appThemeStyle?.[weatherStatus]?.warning?.[item?.Color?.Name?.toLocaleLowerCase()] ?? {};
      return (
        <div key={index} style={{ borderRadius: pxChangeVw("10px") }}>
          <EarlyWarningView
            title={item.Description?.Localized}
            content={`${item?.detail?.start}-${item?.detail?.end}`}
            onClick={() => {}}
            color={warning?.fontColor}
            bgColor={tipsStyle?.backgroundColor}
            fillColor={warning?.fillColor}
            AlertID={item?.AlertID}
          />
        </div>
      );
    });
  };

  return (
    <div className="flex flex-col gap-y-10">
      {renderDaySelector()}

      <div data-position="position-27">{adPosition27}</div>

      <div data-position="position-40" className={classNames([isTranTabAdScenario ? "px-16" : ""])}>
        {adPosition40}
      </div>

      <div className={classNames(["flex flex-col justify-start gap-y-10 box-border p-[0_16px]"])}>
        {renderWarnings()}
        {renderWeatherPeriod("Day", "title10", "Maximum")}

        <div data-position="position-28">{adPosition28}</div>

        <div data-position="position-37">{adPosition37}</div>

        {renderWeatherPeriod("Night", "title11", "Minimum")}

        <div data-position="position-29">{adPosition29}</div>

        <div data-position="position-38">{adPosition38}</div>
        {/* 今日详情/白天晚上指标 */}
        {!!dayItem?.Items?.length && <BaseCard itemList={dayItem.Items} />}
        {/* 空气质量 */}
        {!isEmptyType(dayItem?.aqi) && <ChartData aqi={dayItem.aqi} />}
        {/* 日出日落 */}
        {(dayItem.Sun || dayItem.Moon) && (
          <Sunrise
            forecast1day={{
              Sun: {
                rate: dayItem.Sun.rate,
                Rise: dayItem.Sun.Rise,
                Set: dayItem.Sun.Set,
              },
              Moon: {
                rate: dayItem.Moon.rate,
                Rise: dayItem.Moon.Rise,
                Set: dayItem.Moon.Set,
                NextDay: dayItem.Moon.NextDay,
              },
            }}
            current={{
              IsDayTime: dayItem.isDayTime,
            }}
          />
        )}
      </div>
    </div>
  );
};

export default DailyDetail;
